import { AsyncPipe, DecimalPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import { DotFitter, Fitter } from '@sc/data-access-models';
import {
  FITTER_NAME_DISPLAY_CONTEXT,
  FitterNameDisplayContext,
  FitterNameDisplayService,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { IconComponent } from '@sc/ui-components';
import { BaseGMapOverlay } from '../../../../utils/base-gmap-overlay';

@Component({
  selector: 'sc-gmap-dot-fitters',
  styleUrls: ['./gmap-dot-fitters.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    FitterNameDisplayService,
    {
      provide: FITTER_NAME_DISPLAY_CONTEXT,
      useValue: FitterNameDisplayContext.FitterPopupHeader,
    },
  ],
  imports: [
    NgIf,
    MatTooltipModule,
    IconComponent,
    AsyncPipe,
    DecimalPipe,
    TranslateModule,
    FitterNamePipe,
    PhoneNumberPipe,
  ],
  template: `
    <div
      [class]="getDotFitterClass()"
      [attr.aria-label]="
        'GlobalApplicativeConstants.accessibility.labels.dotFitter'
          | translate
            : {
                fitterName: _dotFitter.brand,
                nbKm: _dotFitter.distance / 1000 | number: '1.1-1',
                fitterPhone: _dotFitter.phone | phoneNumber | async
              }
      "
      [attr.aria-description]="
        (onError
          ? 'GlobalApplicativeConstants.accessibility.labels.dotFitterError'
          : ''
        ) | translate
      "
    >
      <span *ngIf="displayBasicDot()" aria-hidden="true">
        <div
          class="dot-fitter-map-element-dot"
          #tooltip="matTooltip"
          [matTooltip]="
            _dotFitter
              | fitterName
                : undefined
                : { phone: _dotFitter.phone | phoneNumber | async }
              | async
          "
          (click)="tooltip.show(); onFitterClick()"
          [class.dot-fitter-no-slot]="haveNoSlot()"
        ></div>
      </span>
      <span
        class="garage-icon-hover"
        *ngIf="displayBasicHoverIcon()"
        aria-hidden="true"
      >
        <sc-icon
          name="fitter"
          #tooltip="matTooltip"
          [matTooltip]="
            _dotFitter
              | fitterName
                : undefined
                : { phone: _dotFitter.phone | phoneNumber | async }
              | async
          "
          (click)="tooltip.show(); onFitterClick()"
          [class.dot-fitter-no-slot]="haveNoSlot()"
        ></sc-icon>
      </span>
      <span
        class="custom-dot-image"
        *ngIf="displayCustomImage()"
        aria-hidden="true"
      >
        <img
          [src]="getDotImageSrc()"
          alt="custom fitter logo"
          #tooltip="matTooltip"
          [matTooltip]="
            _dotFitter
              | fitterName
                : undefined
                : { phone: _dotFitter.phone | phoneNumber | async }
              | async
          "
          (click)="tooltip.show(); onFitterClick()"
        />
      </span>
    </div>
  `,
})
export class GmapDotFittersComponent
  extends BaseGMapOverlay
  implements OnChanges, OnDestroy
{
  @Input() public map: google.maps.Map;
  @Input() public set dotFitter(newDotFitter: DotFitter) {
    this.position = newDotFitter.location;
    this._dotFitter = newDotFitter;
  }
  @Input() public fitter: Fitter;
  @Input() public onError: boolean;
  @Input() public fitterHover: string;

  @Output() public fitterClickEvent = new EventEmitter<Fitter>();

  public _dotFitter: DotFitter;

  constructor(
    elRef: ElementRef,
    public configurationService: ConfigurationService,
    private readonly assetsService: AssetsService,
    private readonly featureFlippingManagerService: FeatureFlippingManagerService
  ) {
    super(elRef, 'gmap-dot-fitter');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.map?.previousValue && !!changes.map?.currentValue) {
      this.gmap = changes.map.currentValue;
    }

    if (
      changes.fitterHover?.previousValue !== changes.fitterHover?.currentValue
    ) {
      this.updateClass(this.isDotFitterHover() ? 'hover' : '');
    }
  }

  ngOnDestroy(): void {
    this.onRemove();
  }

  isDotImage() {
    return !!this.configurationService.getConfig()?.map?.dotIcon;
  }

  getDotImageSrc() {
    const config = this.configurationService.getConfig();
    return this.assetsService.getAssetPath(
      `${config.id}_${config.map.dotIcon}.svg`
    );
  }

  getDotFitterClass() {
    const classResult = ['dot-fitter-map-element'];

    // Determine basic / cache / error colors
    if (this.onError) {
      classResult.push('dot-fitter-map-element-error');
    } else if (
      !this._dotFitter.displayAppointments &&
      this._dotFitter.cacheAppointments
    ) {
      classResult.push('dot-fitter-map-element-cache');
    } else {
      classResult.push('dot-fitter-map-element-main');
    }

    // Determine hover class
    if (this.isDotFitterHover()) {
      classResult.push('dot-fitter-map-element-hovered');
    }

    return classResult.join(' ');
  }

  isDotFitterHover() {
    return this.fitterHover === (this.fitter?.id || this._dotFitter?.id);
  }

  shouldDisplayBasicIcon() {
    return (
      this.isDotFitterHover() ||
      this.featureFlippingManagerService.isFeatureActivated(
        'map-dot-fitters-always-display-fitter-icon'
      )
    );
  }

  displayBasicDot() {
    return !this.shouldDisplayBasicIcon() && !this.displayCustomImage();
  }

  displayBasicHoverIcon() {
    return this.shouldDisplayBasicIcon() && !this.displayCustomImage();
  }

  displayCustomImage() {
    return this.isDotImage();
  }

  onFitterClick() {
    this.fitterClickEvent.emit(this.fitter);
  }

  haveNoSlot() {
    if (this._dotFitter?.brandId) {
      return (
        this.configurationService.getFitterBrandInfosByBrandId(
          this._dotFitter.brandId
        )?.booking?.search?.version == 'NO_SLOT'
      );
    }
  }
}
